<template>
    <div class="ui-page">
        <ui-page-header title="Votação" subtitle="Votações realizadas nas assembleias de credores.">
            <v-btn color="primary" outlined @click="load(true)">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Gerar Excel
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" color="primary" depressed to="/votacao/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field id="name" v-model="dataList.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="dataList.filter.general_meeting" :items="dataset.generalMeetings" placeholder="Todas as assembleias"
                    item-value="id" item-text="name" dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"
                    no-data-text="Nenhuma assembleia encontrada" @change="applyFilters">
                </v-autocomplete>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhuma votação foi encontrada" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="openRow">

                <template v-slot:[`item.name`]="{ item }">
                    <span class="ui-cell-main">{{ item.name }}</span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="getStatusName(item.status)" :tone="getStatusTone(item.status)" />
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    <ui-badge :text="getTypeName(item.type)" tone="neutral" />
                </template>

                <template v-slot:[`item.generalMeeting`]="{ item }">
                    {{ item.generalMeeting.name }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', disabled: !$check('GeneralMeeting', 'PATCH'), handler: () => viewItem(item) },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, disabled: !$check('GeneralMeeting', 'DELETE'), handler: () => deleteItem(item) },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import { downloadXlsx } from "@/common/util/export-xlsx.util";

import Vue from "vue";
import VueLodash from 'vue-lodash'
import _ from 'lodash';
import axios from 'axios';
import { cpf, cnpj } from 'cpf-cnpj-validator'
import agcVotingStatus from "@/common/data/agc-voting-status.data"
import agcVotingTypes from "@/common/data/agc-voting-types.data"

import object from "@/common/util/object"

Vue.use(VueLodash)

export default {
    name: "VotingList",

    data: () => ({
        dataset: {
            data: {},
            generalMeetings: [],
        },
        agcVotingTypes: agcVotingTypes,
        agcVotingStatus: agcVotingStatus,
        dataList: {
            filter: {
                name: "",
                registration: "",
                client: "",
                code: "",
                status: "",

            },
            headers: [
                { text: "Nome", value: "name" },
                { text: "Assembleia", value: "generalMeeting.name" },
                { text: "Tipo", value: "type" },
                { text: "Status", value: "status" },
                { text: "Criado em", value: "created_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                size: 10,
                itemsPerPage: 10,
                sortBy: ['name'],
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            }
        },
    }),

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },
    },

    created() {
        this.load();
        this.loadGeneralMeetings();
    },

    methods: {
        formatCpfCnpj(value) {
            const digits = String(value || '').replace(/\D/g, '');
            if (!digits) return '';
            return digits.length === 14 ? cnpj.format(digits) : cpf.format(digits);
        },

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = {
                name: "",
                registration: "",
                client: "",
                code: "",
                status: "",
            };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        pageChanged(page) {            
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {    
            this.dataList.options.size = size;
            this.load();
        },

        getStatusName(value) {
            const status = this.agcVotingStatus.find(s => s.id === value);
            return status.name ? status.name : 'N/A';
        },

        getStatusTone(value) {
            const tones = { 1: 'neutral', 2: 'info', 3: 'warning', 4: 'success', 5: 'success' };
            return tones[value] || 'neutral';
        },

        getTypeName(value) {
            const type = this.agcVotingTypes.find(s => s.id === value);
            return type.name ? type.name : 'N/A';
        },
      

        debounce: _.debounce(function () {
            this.reload();
        }, 500),

        reload: function () {
            this.load();
        },


        async load(exportCsv) {

            let search = Object.fromEntries(Object.entries(this.dataList.filter).filter(([_, v]) => v || v === 0))

            search = !object.isEmpty(search) ? search : null;

            this.dataList.loading = true;

            var limit = exportCsv ? 1000000 : (this.dataList.options.itemsPerPage || 10);

            var start = (exportCsv !== undefined && exportCsv) ? 1 : this.dataList.options.page;

            try {
                const result = await axios.get('/api/admin/general-meeting-votings', { params: { page: start, limit: limit, search: search } });

                if(!exportCsv) {
                    this.dataList.data = result.data.content.data;
                    this.dataList.count = result.data.content.paging.total;
                    
                    return;
                }

                this.generateCSV(result.data.content.data)

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }

        },


        async loadGeneralMeetings() {

            try {
                const result = await axios.get(`/api/general-meetings`, { params: { start: 1, limit: 100000, search: {} } });

                const data = result.data.content
                
                data.sort((a, b) => a.name.localeCompare(b.name));
                
                this.dataset.generalMeetings = [{ id: "", name: "Todas " }, ...data];

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        // A linha abre o detalhe so com a mesma permissao do "Editar".
        openRow(item) {
            if (this.$check('GeneralMeeting', 'GET')) this.viewItem(item);
        },

        viewItem(item) {
            const id = item.id
            //this.$router.push(`votacao/${item.id}`);
            this.$router.push({ name: 'VotingDetails', params: { id: id } });
        },

        async deleteItem(item) {

            if (!item || !item.id) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Tem certeza que deseja continuar?',
                title: 'Excluir votação',
                actions: {
                    false: 'Cancelar',
                    true: 'Excluir'
                }
            })

            if (!proceedAction) return;

            this.dataList.loading = true

            try {

                const result = await axios.delete(`/api/admin/general-meeting-votings/${item.id}`)

                this.$dialog.notify.info("Votação excluída com sucesso!", { position: "top-right", timeout: 2000 });

                this.load()

            } catch (error) {

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })

            } finally {
                this.dataList.loading = false;
            }

        },

        async generateCSV(data) {

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja exportar os dados para Excel?',
                title: 'Exportar dados',
                actions: {
                    false: 'Cancelar',
                    true: 'Exportar'
                }
            })

            if (!proceedAction) return;

            if (data.length == 0) {
                this.$dialog.notify.error("Sem dados para exportar!", { position: "top-right", timeout: 2000 });

                return;
            }

            const newData = data.map(objeto => ({
                "NÚMERO": objeto.code,
                "NOME CREDOR": objeto.creditor_name,
                "CPF/CNPJ": this.formatCpfCnpj(objeto.creditor_registration),
                "NOME DEVEDORA": objeto.client.name,
                "RESERVA DE VALOR": objeto.amount_reservation ? objeto.amount_reservation.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "N/A",
                "VALOR DO CRÉDITO": objeto.approved_amount ? objeto.approved_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : "N/A",
                "TIPO": objeto.type === 1 ? "Habilitação de crédito retardatária" : 
                        (objeto.type === 2 ? "Impugnação de crédito retardatária" : 
                        (objeto.type === 3 ? "Impugnação de crédito tempestiva" : "N/A")),
                "STATUS": objeto.status === 1 ? "Deferido" : 
                        (objeto.status === 2 ? "Parcialmente deferido" : 
                        (objeto.status === 3 ? "Indeferido" : 
                        (objeto.status === 4 ? "Extinto" : 
                        (objeto.status === 5 ? "Pendente de julgamento" : "N/A")))),
                "CRIADO EM": objeto.created_at,

            }));

            try{

                await downloadXlsx(newData, 'Votacao', { sheetName: 'Votação' });

                this.$dialog.notify.info("Planilha Excel gerada com sucesso!", { position: "top-right", timeout: 2000 });

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },
    },
};
</script>
